import { colors } from '@repo/theme/primitives';
import type { IIntegrationMetric, IMetricWidgetSample } from '../../../../api';
import { MetricName, useIntegrationMetricList } from '../../../../api';
import { WidgetType } from '../../../../interfaces';
import { LOOKBACK_KEY, useChartColors } from '../../utils/utils';
import { WORKSPACE_ANALYTICS_ALLOWED_WIDGETS_TO_ADD } from './constants';

type IMetricWidgetSampleVariant = Partial<IMetricWidgetSample> &
	Partial<IMetricWidgetSample['metric_metadata']>;

const baseMetricWidgetMetadata = (
	metric: IIntegrationMetric
): Partial<IMetricWidgetSample['metric_metadata']> => ({
	integration_id: metric.integration_id,
	integration_type: metric.integration_type,
	metric_id: metric.id,
	fixed_filter: {
		[LOOKBACK_KEY]: 'weekly',
	},
	user_filter: {},
	filter_options: metric.filter_options,
});

const genTestWidgets = (metric: IIntegrationMetric) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'Test Execution Time',
			group: 'Costs',
			groupIconName: 'coin',
			value: { execution_time: 'avg' },
			suffix: ' seconds',
			type: WidgetType.METRIC_LINE_CHART,
		},
		{
			title: 'Test Results',
			value: {
				fail: 'sum',
				pass: 'sum',
				error: 'sum',
				skipped: 'sum',
				warn: 'sum',
			},
		},
		{
			title: 'Test Failures',
			value: { fail: 'sum' },
		},
		{
			title: 'Test Passes',
			value: { pass: 'sum' },
		},
		{
			title: 'Test Errors',
			value: { error: 'sum' },
		},
		{
			title: 'Test Skips',
			value: { skipped: 'sum' },
		},
		{
			title: 'Test Warnings',
			value: { warn: 'sum' },
		},
	];
	return variants.map(
		(variant) =>
			({
				group: variant.group ?? 'Tests',
				groupIconName: variant.groupIconName ?? 'circleCheck',
				type: variant?.type || WidgetType.METRIC_LINE_CHART,
				title: metric?.integration_name
					? metric.integration_name + ' ' + variant.title
					: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.DBT_TEST_RESULTS,
					suffix: variant?.suffix || ' tests',
					value: variant.value,
					value_color: {
						execution_time: colors.cyan[4],
						fail: colors.orange[4],
						pass: colors.green[4],
						error: colors.red[4],
						skipped: colors.gray[4],
						warn: colors.yellow[4],
					},
				},
			}) as IMetricWidgetSample
	);
};

const genDbtRateByDayWidgets = (metric: IIntegrationMetric) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'DBT Rate (Day)',
			value: {
				run_failure_rate: 'avg',
				test_failure_rate: 'avg',
				build_failure_rate: 'avg',
				average_test_failure_per_failed_run: 'avg',
			},
		},
		{
			title: 'Run Failure Rate (Day)',
			value: { run_failure_rate: 'avg' },
		},
		{
			title: 'Test Failure Rate (Day)',
			value: { test_failure_rate: 'avg' },
		},
		{
			title: 'Build Failure Rate (Day)',
			value: { build_failure_rate: 'avg' },
		},
		{
			title: 'Average Test Failure Per Failed Run (Day)',
			value: { average_test_failure_per_failed_run: 'avg' },
		},
	];
	return variants.map(
		(variant) =>
			({
				group: variant.group ?? 'Tests',
				groupIconName: variant.groupIconName ?? 'circleCheck',
				type: variant?.type || WidgetType.METRIC_LINE_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.DBT_RATES,
					is_percentage: variant.value?.average_test_failure_per_failed_run
						? false
						: true,
					suffix: variant?.suffix || '%',
					value: variant.value,
					value_color: {
						run_failure_rate: colors.red[4],
						test_failure_rate: colors.orange[4],
						build_failure_rate: colors.yellow[4],
						average_test_failure_per_failed_run: colors.green[4],
					},
				},
			}) as IMetricWidgetSample
	);
};

const genDbtRateByWeekWidgets = (metric: IIntegrationMetric) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'DBT Rate (Week)',
			value: {
				run_failure_rate_week: 'avg',
				test_failure_rate_week: 'avg',
				build_failure_rate_week: 'avg',
				average_test_failure_per_failed_run_week: 'avg',
			},
		},
		{
			title: 'Run Failure Rate (Week)',
			value: { run_failure_rate_week: 'avg' },
		},
		{
			title: 'Test Failure Rate (Week)',
			value: { test_failure_rate_week: 'avg' },
		},
		{
			title: 'Build Failure Rate (Week)',
			value: { build_failure_rate_week: 'avg' },
		},
		{
			title: 'Average Test Failure Per Failed Run (Week)',
			value: { average_test_failure_per_failed_run_week: 'avg' },
		},
	];
	return variants.map(
		(variant) =>
			({
				group: variant.group ?? 'Tests',
				groupIconName: variant.groupIconName ?? 'circleCheck',
				type: variant?.type || WidgetType.METRIC_LINE_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.DBT_RATES,
					is_percentage: variant.value?.average_test_failure_per_failed_run_week
						? false
						: true,
					suffix: variant?.suffix || '%',
					value: variant.value,
					value_color: {
						run_failure_rate_week: colors.red[4],
						test_failure_rate_week: colors.orange[4],
						build_failure_rate_week: colors.yellow[4],
						average_test_failure_per_failed_run_week: colors.green[4],
					},
				},
			}) as IMetricWidgetSample
	);
};

const genDbtRateByMonthWidgets = (metric: IIntegrationMetric) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'DBT Rate (Month)',
			value: {
				run_failure_rate_month: 'avg',
				test_failure_rate_month: 'avg',
				build_failure_rate_month: 'avg',
				average_test_failure_per_failed_run_month: 'avg',
			},
		},
		{
			title: 'Run Failure Rate (Month)',
			value: { run_failure_rate_month: 'avg' },
		},
		{
			title: 'Test Failure Rate (Month)',
			value: { test_failure_rate_month: 'avg' },
		},
		{
			title: 'Build Failure Rate (Month)',
			value: { build_failure_rate_month: 'avg' },
		},
		{
			title: 'Average Test Failure Per Failed Run (Month)',
			value: { average_test_failure_per_failed_run_month: 'avg' },
		},
	];
	return variants.map(
		(variant) =>
			({
				group: variant.group ?? 'Tests',
				groupIconName: variant.groupIconName ?? 'circleCheck',
				type: variant?.type || WidgetType.METRIC_LINE_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.DBT_RATES,
					is_percentage: variant.value
						?.average_test_failure_per_failed_run_month
						? false
						: true,
					suffix: variant?.suffix || '%',
					value: variant.value,
					value_color: {
						run_failure_rate_month: colors.red[4],
						test_failure_rate_month: colors.orange[4],
						build_failure_rate_month: colors.yellow[4],
						average_test_failure_per_failed_run_month: colors.green[4],
					},
				},
			}) as IMetricWidgetSample
	);
};

const genModelTestWidgets = (metric: IIntegrationMetric) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'Model Test Coverage',
			value: { has_test: 'max' },
		},
		{
			title: 'Model Test Failures',
			value: { test_failed: 'sum' },
		},
	];
	return variants.map(
		(variant) =>
			({
				group: 'Tests',
				groupIconName: 'circleCheck',
				type: variant?.type || WidgetType.METRIC_LINE_CHART,
				title: metric?.integration_name
					? metric.integration_name + ' ' + variant.title
					: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.DBT_TABLE_TEST_RESULTS,
					suffix: 'has_test' in (variant.value ?? {}) ? '' : ' models',
					value: variant.value,
					is_percentage: 'has_test' in (variant.value ?? {}) ? true : false,
				},
			}) as IMetricWidgetSample
	);
};

// BigQuery Compute Cost Widgets
export const genBigQueryComputeWidgets = (
	computeCostMetric: IIntegrationMetric,
	chartColors: string[]
) => {
	const computeCostVariants: IMetricWidgetSampleVariant[] = [
		{
			title: 'BigQuery Compute Costs',
			value: {
				estimated_cost_on_demand: 'sum',
				estimated_cost_capacity_based: 'sum',
			},
			prefix: '$',
		},
		{
			title: 'BigQuery Compute Cost (On-Demand)',
			value: { estimated_cost_on_demand: 'sum' },
			prefix: '$',
		},
		{
			title: 'BigQuery Compute Cost (Capacity-Based)',
			value: { estimated_cost_capacity_based: 'sum' },
			prefix: '$',
		},
	];

	return computeCostVariants.map(
		(variant) =>
			({
				group: 'Costs',
				groupIconName: 'coin',
				type: variant.type || WidgetType.METRIC_LINE_CHART,
				title: variant.title,
				metric_metadata: {
					...baseMetricWidgetMetadata(computeCostMetric),
					metric_name: MetricName.BIGQUERY_COMPUTE_COST,
					value: variant.value,
					value_color: {
						estimated_cost_on_demand: chartColors[0],
						estimated_cost_capacity_based: chartColors[1],
					},
					prefix: variant.prefix,
				},
			}) as IMetricWidgetSample
	);
};

// BigQuery Storage Cost Widgets
export const genBigQueryStorageWidgets = (
	storageCostMetric: IIntegrationMetric,
	chartColors: string[]
) => {
	const storageCostVariants: IMetricWidgetSampleVariant[] = [
		{
			title: 'BigQuery Storage Costs per Month',
			value: { estimated_storage_cost: 'sum' },
			prefix: '$',
			type: WidgetType.METRIC_SINGLE_VALUE,
		},
		{
			title: 'BigQuery Storage Usage per Month',
			value: {
				active_logical: 'sum',
				active_physical: 'sum',
				long_term_logical: 'sum',
				long_term_physical: 'sum',
			},
			type: WidgetType.METRIC_BAR_CHART,
		},
		{
			title: 'BigQuery Active Storage Usage (Logical) per Month',
			value: { active_logical: 'sum' },
		},
		{
			title: 'BigQuery Active Storage Usage (Physical) per Month',
			value: { active_physical: 'sum' },
		},
		{
			title: 'BigQuery Long-Term Storage Usage (Logical) per Month',
			value: { long_term_logical: 'sum' },
		},
		{
			title: 'BigQuery Long-Term Storage Usage (Physical) per Month',
			value: { long_term_physical: 'sum' },
		},
	];

	return storageCostVariants.map(
		(variant) =>
			({
				group: 'Costs',
				groupIconName: 'coin',
				type: variant.type || WidgetType.METRIC_SINGLE_VALUE,
				title: variant.title,
				metric_metadata: {
					...baseMetricWidgetMetadata(storageCostMetric),
					metric_name: MetricName.BIGQUERY_STORAGE_COST,
					value: variant.value,
					value_color: {
						active_logical: chartColors[0],
						active_physical: chartColors[1],
						long_term_logical: chartColors[2],
						long_term_physical: chartColors[3],
					},
					prefix: variant.prefix,
					suffix: variant.suffix,
				},
			}) as IMetricWidgetSample
	);
};

const genSnowflakeCostWidgets = (
	metric: IIntegrationMetric,
	chartColors: string[]
) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'Snowflake Costs',
			value: { cost: 'sum' },
			prefix: '$',
		},
		{
			title: 'Snowflake Costs Total',
			value: { cost: 'sum' },
			prefix: '$',
			type: WidgetType.METRIC_SINGLE_VALUE,
		},
	];
	return variants.map(
		(variant) =>
			({
				group: 'Costs',
				groupIconName: 'coin',
				type: variant.type || WidgetType.METRIC_LINE_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.SNOWFLAKE_COST,
					value: variant.value,
					value_color: {
						cost: chartColors[0],
					},
					prefix: variant.prefix,
				},
			}) as IMetricWidgetSample
	);
};

export const genSnowflakeCreditPerWarehouseWidgets = (
	metric: IIntegrationMetric,
	chartColors: string[]
) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'Snowflake Warehouse Metering History',
			value: { compute_credits: 'sum', cloud_services_credits: 'sum' },
		},
	];
	return variants.map(
		(variant) =>
			({
				group: 'Costs',
				groupIconName: 'coin',
				type: WidgetType.METRIC_LINE_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.SNOWFLAKE_CREDITS_PER_WAREHOUSE,
					value: variant.value,
					value_color: {
						compute_credits: chartColors[0],
						cloud_services_credits: chartColors[1],
					},
					suffix: ' credits',
				},
			}) as IMetricWidgetSample
	);
};

export const genSnowflakeCreditPerUserWidgets = (
	metric: IIntegrationMetric
) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'Snowflake User Query Credits',
			value: { credits: 'sum' },
		},
	];
	return variants.map(
		(variant) =>
			({
				group: 'Costs',
				groupIconName: 'coin',
				type: WidgetType.METRIC_LINE_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.SNOWFLAKE_CREDITS_PER_USER,
					value: variant.value,
					suffix: ' credits',
				},
			}) as IMetricWidgetSample
	);
};

export const genSnowflakeQueryVolumeWidgets = (metric: IIntegrationMetric) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'Snowflake Query Volume',
			value: { query_count: 'sum' },
		},
		{
			title: 'Snowflake Average Daily Query Volume',
			value: { query_count: 'avg' },
			type: WidgetType.METRIC_SINGLE_VALUE,
		},
	];
	return variants.map(
		(variant) =>
			({
				group: 'Costs',
				groupIconName: 'coin',
				type: variant.type || WidgetType.METRIC_LINE_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.SNOWFLAKE_QUERY_VOLUME,
					value: variant.value,
					suffix: ' queries',
				},
			}) as IMetricWidgetSample
	);
};

export const genSnowflakeStorageCreditsWidgets = (
	metric: IIntegrationMetric,
	chartColors: string[]
) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'Snowflake Storage Credits Usage',
			value: { average_database_bytes: 'sum', average_failsafe_bytes: 'sum' },
		},
	];
	return variants.map(
		(variant) =>
			({
				group: 'Costs',
				groupIconName: 'coin',
				type: variant.type || WidgetType.METRIC_LINE_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					is_bytes: true,
					metric_name: MetricName.SNOWFLAKE_STORAGE_USAGE,
					value: variant.value,
					value_color: {
						average_database_bytes: chartColors[0],
						average_failsafe_bytes: chartColors[1],
					},
				},
			}) as IMetricWidgetSample
	);
};

export const genSnowflakeComputeCreditsWidgets = (
	metric: IIntegrationMetric,
	chartColors: string[]
) => {
	const variants: IMetricWidgetSampleVariant[] = [
		{
			title: 'Snowflake Compute Credits Usage',
			value: {
				compute_credits: 'sum',
				cloud_services_credits: 'sum',
				adjustment_cloud_services_credits: 'sum',
			},
		},
	];
	return variants.map(
		(variant) =>
			({
				group: 'Costs',
				groupIconName: 'coin',
				type: variant?.type || WidgetType.METRIC_BAR_CHART,
				title: variant.title,

				metric_metadata: {
					...baseMetricWidgetMetadata(metric),
					metric_name: MetricName.SNOWFLAKE_COMPUTE_USAGE,
					value: variant.value,
					value_color: {
						compute_credits: chartColors[0],
						cloud_services_credits: chartColors[1],
						adjustment_cloud_services_credits: chartColors[2],
					},
				},
			}) as IMetricWidgetSample
	);
};

function sortAlphabetically(
	items: IMetricWidgetSample[]
): IMetricWidgetSample[] {
	return items.sort((a, b) => {
		const titleA = (typeof a === 'string' ? a : a.title).toLowerCase();
		const titleB = (typeof b === 'string' ? b : b.title).toLowerCase();
		return titleA.localeCompare(titleB);
	});
}

export const useWidgetsToAdd = () => {
	const chartColors = useChartColors();
	const { data: metrics } = useIntegrationMetricList({
		options: {
			select: (data) => data.results,
		},
	});

	let widgetSamples = [...WORKSPACE_ANALYTICS_ALLOWED_WIDGETS_TO_ADD];

	// TODO: cleanup logic https://github.com/secoda/secoda/pull/2091#discussion_r1388179528
	const testMetrics =
		metrics?.filter((metric) => metric.metric_type === 'dbt_test') ?? [];

	if (testMetrics.length > 0) {
		widgetSamples = widgetSamples.concat(
			testMetrics.flatMap((metric) => genTestWidgets(metric))
		);
	}

	const tableTestMetrics =
		metrics?.filter((metric) => metric.metric_type === 'dbt_table_test') ?? [];

	if (tableTestMetrics.length > 0) {
		widgetSamples = widgetSamples.concat(
			tableTestMetrics.flatMap((metric) => genModelTestWidgets(metric))
		);
	}

	const dbtRateMetric = metrics?.find(
		(metric) => metric.metric_type === 'dbt_rates'
	);

	if (dbtRateMetric) {
		widgetSamples = widgetSamples.concat(genDbtRateByDayWidgets(dbtRateMetric));
		widgetSamples = widgetSamples.concat(
			genDbtRateByWeekWidgets(dbtRateMetric)
		);
		widgetSamples = widgetSamples.concat(
			genDbtRateByMonthWidgets(dbtRateMetric)
		);
	}

	const computeCostMetric = metrics?.find(
		(metric) => metric.metric_type === 'bigquery_compute_cost'
	);
	const storageCostMetric = metrics?.find(
		(metric) => metric.metric_type === 'bigquery_storage_cost'
	);

	if (computeCostMetric) {
		widgetSamples = widgetSamples.concat(
			genBigQueryComputeWidgets(computeCostMetric, chartColors)
		);
	}
	if (storageCostMetric) {
		widgetSamples = widgetSamples.concat(
			genBigQueryStorageWidgets(storageCostMetric, chartColors)
		);
	}

	const snowflakeCostMetric = metrics?.find(
		(metric) => metric.metric_type === 'snowflake_cost'
	);

	if (snowflakeCostMetric) {
		widgetSamples = widgetSamples.concat(
			genSnowflakeCostWidgets(snowflakeCostMetric, chartColors)
		);
	}

	const snowflakeCreditsPerWarehouseMetric = metrics?.find(
		(metric) => metric.metric_type === 'snowflake_credits_per_warehouse'
	);

	if (snowflakeCreditsPerWarehouseMetric) {
		widgetSamples = widgetSamples.concat(
			genSnowflakeCreditPerWarehouseWidgets(
				snowflakeCreditsPerWarehouseMetric,
				chartColors
			)
		);
	}

	const snowflakeCreditsPerUserMetric = metrics?.find(
		(metric) => metric.metric_type === 'snowflake_credits_per_user'
	);

	if (snowflakeCreditsPerUserMetric) {
		widgetSamples = widgetSamples.concat(
			genSnowflakeCreditPerUserWidgets(snowflakeCreditsPerUserMetric)
		);
	}

	const snowflakeQueryVolumeMetric = metrics?.find(
		(metric) => metric.metric_type === 'snowflake_query_volume'
	);

	if (snowflakeQueryVolumeMetric) {
		widgetSamples = widgetSamples.concat(
			genSnowflakeQueryVolumeWidgets(snowflakeQueryVolumeMetric)
		);
	}

	const snowflakeStorageCreditsMetric = metrics?.find(
		(metric) => metric.metric_type === 'snowflake_storage_usage'
	);

	if (snowflakeStorageCreditsMetric) {
		widgetSamples = widgetSamples.concat(
			genSnowflakeStorageCreditsWidgets(
				snowflakeStorageCreditsMetric,
				chartColors
			)
		);
	}

	const snowflakeComputeCreditsMetric = metrics?.find(
		(metric) => metric.metric_type === 'snowflake_compute_usage'
	);

	if (snowflakeComputeCreditsMetric) {
		widgetSamples = widgetSamples.concat(
			genSnowflakeComputeCreditsWidgets(
				snowflakeComputeCreditsMetric,
				chartColors
			)
		);
	}

	return sortAlphabetically(widgetSamples);
};
